import * as React from 'react';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getImageUrl } from '@/utils/urls';
import { getItem } from '@/redux/modules/item';
import Header from './Header';
import ImageZoom from '@/components/ImageZoom/ImageZoom';
import loadable from '@loadable/component';
import MobileItemGallery from './MobileItemGallery';
import styled from 'styled-components';
import ThumbnailScroller from './ThumbnailScroller';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import usePrevious from '@/hooks/usePrevious';
import { useSelector } from 'react-redux';

const Lightbox = loadable(() => import('react-spring-lightbox'));

type Props = {
    itemId: number;
};

const ItemGallery = ({ itemId }: Props) => {
    const { catalogId, imageVersion, photos = [], sellerId, title } = useSelector((state) => getItem(state, itemId));
    const mobile = useIsNarrowerThan(768);
    const [showLightBox, setShowLightBox] = React.useState(false);

    const activateLightbox = React.useCallback(() => {
        setShowLightBox(true);
    }, []);
    const deactivateLightbox = React.useCallback(() => {
        setShowLightBox(false);
    }, []);

    const [imageGalleryIndex, setImageGalleryIndex] = React.useState(0);

    const previousItemId = usePrevious(itemId);
    React.useEffect(() => {
        if (previousItemId !== itemId) {
            setImageGalleryIndex(0);
        }
    }, [itemId, previousItemId]);

    const generateImageUrls = React.useCallback(() => {
        return photos.map((imgNumber) => {
            return {
                alt: title,
                msrc: getImageUrl({ catalogId, imageNumber: imgNumber, imageVersion, itemId, quality: 15, sellerId }),
                src: getImageUrl({ catalogId, imageNumber: imgNumber, imageVersion, itemId, quality: 80, sellerId }),
            };
        });
    }, [catalogId, imageVersion, itemId, photos, sellerId, title]);

    const images = generateImageUrls() || [];

    const gotoPrevious = React.useCallback(() => {
        const canPrev = imageGalleryIndex > 0;
        if (canPrev) {
            setImageGalleryIndex(imageGalleryIndex - 1);
        } else {
            setImageGalleryIndex(images.length - 1);
        }
    }, [imageGalleryIndex, images.length]);

    const gotoNext = React.useCallback(() => {
        const canNext = imageGalleryIndex < images.length - 1;
        if (canNext) {
            setImageGalleryIndex(imageGalleryIndex + 1);
        } else {
            setImageGalleryIndex(0);
        }
    }, [imageGalleryIndex, images.length]);

    const activeImageUrl = images[imageGalleryIndex] ? images[imageGalleryIndex].src : '';
    const activeImagePlaceholderUrl = images[imageGalleryIndex] ? images[imageGalleryIndex].msrc : '';
    const mainImageAlt = imageGalleryIndex ? `${title} - ${imageGalleryIndex + 1}` : title;

    return (
        <>
            {!mobile && (
                <StyledGallery>
                    <StyledThumbnailScroller
                        activeIndex={imageGalleryIndex}
                        itemId={itemId}
                        onClick={setImageGalleryIndex}
                    />
                    <StyledImageZoom
                        className="item-image-primary"
                        imageAlt={mainImageAlt}
                        imageUrl={activeImageUrl}
                        itemId={itemId}
                        onClick={activateLightbox}
                        placeholderImageUrl={activeImagePlaceholderUrl}
                        resetFlag={showLightBox}
                    />
                </StyledGallery>
            )}
            {mobile && (
                <MobileItemGallery
                    activeIndex={imageGalleryIndex}
                    itemId={itemId}
                    onImageIndexChange={setImageGalleryIndex}
                    setShowLightBox={activateLightbox}
                />
            )}
            {Boolean(images.length) && (
                <StyledLightbox
                    currentIndex={imageGalleryIndex}
                    images={images}
                    isOpen={showLightBox}
                    onClose={deactivateLightbox}
                    onNext={gotoNext}
                    onPrev={gotoPrevious}
                    renderHeader={() => (
                        <Header
                            currentImageIndex={imageGalleryIndex}
                            galleryLength={images.length}
                            galleryTitle={title}
                            itemId={itemId}
                            onClose={deactivateLightbox}
                        />
                    )}
                    renderNextButton={() => (
                        <ArrowButton $position="right" onClick={gotoNext} role="button">
                            <FontAwesomeIcon icon={faChevronRight} />
                        </ArrowButton>
                    )}
                    renderPrevButton={() => (
                        <ArrowButton $position="left" onClick={gotoPrevious} role="button">
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </ArrowButton>
                    )}
                    singleClickToZoom
                />
            )}
        </>
    );
};

export default React.memo<Props>(ItemGallery);

const StyledLightbox = styled(Lightbox)`
    background: ${({ theme }) => theme.colors.white};
    img {
        cursor: zoom-in;
    }
`;

const StyledGallery = styled.div`
    display: flex;
    flex-direction: row;
    max-height: 550px;
    height: 550px;

    @media print {
        max-height: 360px;
        height: 360px;
    }
`;

const StyledImageZoom = styled(ImageZoom)`
    max-height: 550px;
    height: 550px;
    background-color: ${({ theme }) => theme.colors.white};
`;

const StyledThumbnailScroller = styled(ThumbnailScroller)`
    height: 550px;
`;

const ArrowButton = styled.span<{ $position: string }>`
    position: absolute;
    left: ${({ $position }) => ($position === 'left' ? 0 : 'unset')};
    right: ${({ $position }) => ($position === 'right' ? 0 : 'unset')};
    padding: 10px 20px;
    z-index: 10;
    font-size: 35px;

    :hover {
        cursor: pointer;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        display: none;
    }
`;
